import { useQuery } from "@tanstack/react-query";
import { Account, api, Me, Role } from "../api";

// Mirrors services/api/src/blinkfree_api/auth.py.
// IntraScene staff: viewer < admin < dev, each a superset of the previous one.
// Customer people: viewer (watch, search, play back, alerts) < owner (also
// manages the account's people, locations, recorders, cameras and boxes).
// A staff member viewing a customer account is that account's owner.
const STAFF_RANK: Record<string, number> = { viewer: 1, admin: 2, dev: 3 };

export type UseRole = {
  role: Role;
  isLoading: boolean;
  /** IntraScene staff acting as staff. */
  isStaff: boolean;
  /** IntraScene staff, including while viewing a customer account. */
  isRealStaff: boolean;
  /** Viewing the app as one customer account. */
  viewingAs: boolean;
  /** The customer account this session sees; null for the staff view. */
  account: Account | null;
  /** May change things in what this session sees (owner, staff admin/dev). */
  canManage: boolean;
  /** Staff admin or dev: IntraScene management surfaces. */
  isAdmin: boolean;
  /** Staff dev: technical / operations surfaces. */
  isDev: boolean;
  /** Staff rank check; customers never pass. */
  atLeast: (r: "viewer" | "admin" | "dev") => boolean;
};

export function useRole(): UseRole {
  const me = useQuery<Me>({ queryKey: ["me"], queryFn: api.me });
  const role = (me.data?.role ?? "viewer") as Role;
  const isStaff = Boolean(me.data?.is_staff);
  const rank = isStaff ? (STAFF_RANK[role] ?? 0) : 0;
  return {
    role,
    isLoading: me.isLoading,
    isStaff,
    isRealStaff: isStaff || Boolean(me.data?.viewing_as),
    viewingAs: Boolean(me.data?.viewing_as),
    account: me.data?.customer ?? null,
    canManage: role === "owner" || (isStaff && rank >= STAFF_RANK.admin),
    isAdmin: rank >= STAFF_RANK.admin,
    isDev: rank >= STAFF_RANK.dev,
    atLeast: (r) => rank >= (STAFF_RANK[r] ?? 99),
  };
}
